/**
 * Cross-matter deadline aggregation.
 *
 * Pulls every meaningful date the staff cares about and merges them into a
 * single ordered list bucketed by severity. Sources:
 *   - Active visa expiries (client_visas.visa_expiry_date where status=active)
 *   - Lodgement target dates (matters.lodgement_date)
 *   - RFI deadlines (matters.rfi_deadline)
 *
 * RLS keeps staff-only access through the existing matter and contact policies.
 */

import { supabase } from './supabase';

export type DeadlineKind = 'visa_expiry' | 'lodgement_target' | 'rfi_deadline';
export type Severity = 'critical' | 'attention' | 'upcoming' | 'none';

export interface Deadline {
  kind: DeadlineKind;
  /** ISO date YYYY-MM-DD */
  date: string;
  /** Days from today (negative = past) */
  daysAway: number;
  severity: Severity;
  /** The matter this deadline is anchored to (null if a visa not linked to any current matter). */
  matterId: string | null;
  matterCode: string | null;
  matterVisa: string | null;
  /** The contact (applicant or sponsor) the deadline relates to. */
  contactId: string | null;
  contactName: string | null;
  /** Friendly summary line (e.g. "500 Student", "Lodgement target", "RFI response due"). */
  label: string;
  /** Optional secondary line (conditions, sponsor info, etc.). */
  detail: string | null;
}

interface ContactRow {
  id: string;
  given_names: string | null;
  family_name: string | null;
}

interface MatterRow {
  id: string;
  matter_id: string | null;
  visa_subclass: string;
  variant: string | null;
  lodgement_date: string | null;
  rfi_deadline: string | null;
  primary_contact_id: string;
  sponsor_contact_id: string | null;
  primary_contact: ContactRow | null;
}

interface VisaRow {
  id: string;
  contact_id: string;
  visa_subclass: string;
  variant: string | null;
  visa_expiry_date: string | null;
  status: string;
  is_bridging: boolean;
  conditions: string | null;
  contact: ContactRow | null;
}

const CRITICAL_DAYS = 7;
const ATTENTION_DAYS = 14;
const UPCOMING_DAYS = 30;

function daysFromToday(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function severityFor(days: number): Severity {
  if (days < 0) return 'critical';
  if (days <= CRITICAL_DAYS) return 'critical';
  if (days <= ATTENTION_DAYS) return 'attention';
  if (days <= UPCOMING_DAYS) return 'upcoming';
  return 'none';
}

function fullName(c: ContactRow | null): string {
  if (!c) return 'Unknown';
  return [c.given_names, c.family_name].filter(Boolean).join(' ').trim() || 'Unnamed';
}

/**
 * Fetch all upcoming deadlines (within UPCOMING_DAYS), staff-scoped.
 * Optionally filtered to a single matter.
 */
export async function fetchDeadlines(opts?: { matterId?: string }): Promise<Deadline[]> {
  const out: Deadline[] = [];

  // 1. Visa expiries — need to JOIN to a matter to know which matter to deep-link.
  // For staff view: include visas regardless of matter. For per-matter view:
  // include visas whose contact is the matter's primary or sponsor.
  let visaContactIds: string[] | null = null;
  if (opts?.matterId) {
    const { data: m } = await supabase
      .from('matters')
      .select('primary_contact_id, sponsor_contact_id')
      .eq('id', opts.matterId)
      .maybeSingle();
    if (m) {
      visaContactIds = [m.primary_contact_id, m.sponsor_contact_id].filter(
        (x): x is string => x !== null,
      );
    }
  }

  let visaQuery = supabase
    .from('client_visas')
    .select(
      `id, contact_id, visa_subclass, variant, visa_expiry_date, status, is_bridging, conditions,
       contact:contacts!client_visas_contact_id_fkey (id, given_names, family_name)`,
    )
    .eq('status', 'active')
    .not('visa_expiry_date', 'is', null);

  if (visaContactIds) {
    if (visaContactIds.length === 0) {
      // No contacts: skip the visa fetch entirely.
      visaQuery = visaQuery.eq('contact_id', '00000000-0000-0000-0000-000000000000');
    } else {
      visaQuery = visaQuery.in('contact_id', visaContactIds);
    }
  }

  const { data: visas, error: vErr } = await visaQuery;
  if (vErr) throw vErr;

  // Find associated matters for visa contacts (for deep-linking from cross-matter view).
  const visaContactSet = new Set((visas as VisaRow[] | null)?.map((v) => v.contact_id) ?? []);
  const mattersByContact = new Map<string, { id: string; matter_id: string | null; visa_subclass: string }>();
  if (visaContactSet.size > 0) {
    const { data: mRows } = await supabase
      .from('matters')
      .select('id, matter_id, visa_subclass, primary_contact_id, sponsor_contact_id')
      .is('deleted_at', null)
      .or(
        Array.from(visaContactSet)
          .map((id) => `primary_contact_id.eq.${id},sponsor_contact_id.eq.${id}`)
          .join(','),
      );
    for (const m of (mRows ?? []) as Array<{ id: string; matter_id: string | null; visa_subclass: string; primary_contact_id: string; sponsor_contact_id: string | null }>) {
      if (visaContactSet.has(m.primary_contact_id)) {
        mattersByContact.set(m.primary_contact_id, m);
      }
      if (m.sponsor_contact_id && visaContactSet.has(m.sponsor_contact_id) && !mattersByContact.has(m.sponsor_contact_id)) {
        mattersByContact.set(m.sponsor_contact_id, m);
      }
    }
  }

  for (const v of (visas as VisaRow[] | null) ?? []) {
    if (!v.visa_expiry_date) continue;
    const days = daysFromToday(v.visa_expiry_date);
    const sev = severityFor(days);
    if (sev === 'none' && days > 0) continue;
    const linkedMatter = mattersByContact.get(v.contact_id) ?? null;
    out.push({
      kind: 'visa_expiry',
      date: v.visa_expiry_date,
      daysAway: days,
      severity: sev,
      matterId: linkedMatter?.id ?? null,
      matterCode: linkedMatter?.matter_id ?? null,
      matterVisa: linkedMatter?.visa_subclass ?? null,
      contactId: v.contact_id,
      contactName: fullName(v.contact),
      label: v.is_bridging
        ? `${v.visa_subclass} (bridging)`
        : `${v.visa_subclass}${v.variant ? ` ${v.variant}` : ''}`,
      detail: v.conditions ? `Conditions: ${v.conditions}` : null,
    });
  }

  // 2. Matter-anchored deadlines (lodgement target + RFI).
  let matterQuery = supabase
    .from('matters')
    .select(
      `id, matter_id, visa_subclass, variant, lodgement_date, rfi_deadline, primary_contact_id, sponsor_contact_id,
       primary_contact:contacts!matters_primary_contact_id_fkey (id, given_names, family_name)`,
    )
    .is('deleted_at', null);
  if (opts?.matterId) {
    matterQuery = matterQuery.eq('id', opts.matterId);
  }

  const { data: matters, error: mErr } = await matterQuery;
  if (mErr) throw mErr;

  for (const m of (matters as MatterRow[] | null) ?? []) {
    const visaLabel = `${m.visa_subclass}${m.variant ? ` ${m.variant}` : ''}`;
    if (m.lodgement_date) {
      const days = daysFromToday(m.lodgement_date);
      const sev = severityFor(days);
      if (sev !== 'none' || days < 0) {
        out.push({
          kind: 'lodgement_target',
          date: m.lodgement_date,
          daysAway: days,
          severity: sev,
          matterId: m.id,
          matterCode: m.matter_id,
          matterVisa: m.visa_subclass,
          contactId: m.primary_contact_id,
          contactName: fullName(m.primary_contact),
          label: `Lodgement target — ${visaLabel}`,
          detail: null,
        });
      }
    }
    if (m.rfi_deadline) {
      const days = daysFromToday(m.rfi_deadline);
      const sev = severityFor(days);
      if (sev !== 'none' || days < 0) {
        out.push({
          kind: 'rfi_deadline',
          date: m.rfi_deadline,
          daysAway: days,
          severity: sev,
          matterId: m.id,
          matterCode: m.matter_id,
          matterVisa: m.visa_subclass,
          contactId: m.primary_contact_id,
          contactName: fullName(m.primary_contact),
          label: `RFI response due — ${visaLabel}`,
          detail: 'Department has requested information. Reply by this date.',
        });
      }
    }
  }

  // Sort: most-urgent first (smallest daysAway, including negatives).
  out.sort((a, b) => a.daysAway - b.daysAway);

  return out;
}

export function bucketBySeverity(items: Deadline[]) {
  return {
    critical: items.filter((d) => d.severity === 'critical'),
    attention: items.filter((d) => d.severity === 'attention'),
    upcoming: items.filter((d) => d.severity === 'upcoming'),
  };
}

export function relativeDays(days: number): string {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 30) return `in ${days}d`;
  return `in ~${Math.round(days / 30)}mo`;
}

export const KIND_LABEL: Record<DeadlineKind, string> = {
  visa_expiry: 'Visa expiry',
  lodgement_target: 'Lodgement target',
  rfi_deadline: 'RFI deadline',
};
